import type { SerializedToken, GlobalIllumination } from '@roll1/shared';
import type { LitZone } from './lighting';

// --- Types ---

/** Effective vision after accounting for darkvision, lighting, and conditions */
export interface EffectiveVision {
  /** Bright vision range in canvas pixels (fully clear area) */
  brightRangePx: number;
  /** Dim vision range in canvas pixels (50% fog area, e.g. darkvision in dark) */
  dimRangePx: number;
  /** Maximum effective range for visibility polygon clamp, in px (max of bright + dim) */
  effectiveRangePx: number;
  /** Whether vision is completely disabled (blinded, unconscious, dark w/o darkvision/light) */
  disabled: boolean;
}

// --- Condition constants ---

/** Conditions that fully disable token vision */
const VISION_DISABLING_CONDITIONS: ReadonlySet<string> = new Set([
  'blinded',
  'unconscious',
]);

// --- Public API ---

/**
 * Check if token vision is disabled by D&D 5e conditions.
 * Blinded and Unconscious both prevent the token from seeing.
 */
export function isVisionDisabledByCondition(conditions: string[]): boolean {
  for (const c of conditions) {
    if (VISION_DISABLING_CONDITIONS.has(c)) return true;
  }
  return false;
}

/**
 * Compute effective vision for a token, accounting for:
 * - Base vision range (token.vision.range)
 * - Darkvision (token.vision.darkvision)
 * - Global illumination level (bright/dim/dark)
 * - Nearby lit zones (light sources within range)
 * - Conditions (blinded, unconscious)
 *
 * All output ranges are in canvas pixels.
 *
 * @param token - The token to compute vision for
 * @param globalIllumination - Page-level illumination
 * @param litZones - Computed lit zones (null in bright mode)
 * @param gridSize - Grid cell size in canvas pixels
 * @param gridSizeFt - Grid cell size in feet (usually 5)
 * @returns EffectiveVision with ranges in canvas pixels
 */
export function computeEffectiveVision(
  token: SerializedToken,
  globalIllumination: GlobalIllumination,
  litZones: LitZone[] | null,
  gridSize: number,
  gridSizeFt: number,
): EffectiveVision {
  const DISABLED: EffectiveVision = {
    brightRangePx: 0,
    dimRangePx: 0,
    effectiveRangePx: 0,
    disabled: true,
  };

  // No vision data → disabled
  if (!token.vision?.enabled) return DISABLED;

  // Conditions override
  if (isVisionDisabledByCondition(token.conditions)) return DISABLED;

  const ftToPx = gridSize / gridSizeFt;
  const visionRangePx = token.vision.range > 0 ? token.vision.range * ftToPx : 0;
  const darkvisionPx = token.vision.darkvision > 0 ? token.vision.darkvision * ftToPx : 0;
  const level = globalIllumination.level;

  // Check if token is near any light source (simplified: check if any lit zone exists)
  const hasNearbyLight = litZones !== null && litZones.length > 0;

  let brightRangePx: number;
  let dimRangePx: number;

  switch (level) {
    case 'bright':
      // Bright mode: everything within vision range is bright
      brightRangePx = visionRangePx;
      dimRangePx = 0; // no dim zone needed
      break;

    case 'dim':
      // Dim mode: base vision is dim, darkvision upgrades dim→bright
      if (darkvisionPx > 0) {
        // Darkvision in dim → treats dim as bright within darkvision range
        brightRangePx = Math.min(darkvisionPx, visionRangePx || darkvisionPx);
        // Beyond darkvision range: still dim (if vision range extends further)
        dimRangePx = visionRangePx > 0
          ? Math.max(0, visionRangePx - brightRangePx)
          : 0; // unlimited base but bright covers darkvision
      } else {
        // No darkvision: everything is dim within vision range
        brightRangePx = 0;
        dimRangePx = visionRangePx;
      }
      break;

    case 'dark':
      // Dark mode: nothing visible unless darkvision or light
      if (darkvisionPx > 0) {
        // Darkvision in dark → pseudo-dim (treats dark as dim)
        brightRangePx = 0;
        dimRangePx = Math.min(darkvisionPx, visionRangePx || darkvisionPx);
      } else if (hasNearbyLight) {
        // Has nearby light but no darkvision — light zones handle visibility
        // Token can see in lit areas within vision range
        brightRangePx = 0;
        dimRangePx = 0;
        // effectiveRange still applies for the polygon clamp
      } else {
        // No darkvision, no nearby lights in dark → completely blind
        return DISABLED;
      }
      break;

    default:
      return DISABLED;
  }

  // effectiveRangePx: the maximum distance at which anything is visible.
  // Used to clamp the visibility polygon.
  // Must cover both darkvision range AND the furthest light source reach,
  // because in dark mode the player can see lit areas beyond darkvision.
  const maxVisionPx = brightRangePx + dimRangePx;
  let effectiveRangePx: number;
  if (token.vision.range === 0 && level === 'bright') {
    effectiveRangePx = 0; // 0 = unlimited in computeVisibilityPolygon
  } else if (level === 'bright') {
    effectiveRangePx = visionRangePx; // bright: full base range
  } else {
    // dim/dark: must include both darkvision AND light zone reach.
    // A torch 30ft away with 20ft radius means player can see 50ft out.
    // Compute max distance to any lit zone edge from this token.
    let maxLitReachPx = 0;
    if (litZones) {
      const ox = token.x, oy = token.y;
      for (const zone of litZones) {
        const poly = zone.dimPolygon.length >= 3 ? zone.dimPolygon : zone.brightPolygon;
        // Only count a lit zone if the token can reach it with intrinsic vision (darkvision).
        // A zone whose closest vertex is beyond darkvision range is behind walls or too far —
        // the token cannot see into it, so it must not expand effectiveRangePx.
        let minDist = Infinity;
        for (const p of poly) {
          const d = Math.sqrt((p.x - ox) * (p.x - ox) + (p.y - oy) * (p.y - oy));
          if (d < minDist) minDist = d;
        }
        if (minDist > maxVisionPx) continue;
        for (const p of poly) {
          const d = Math.sqrt((p.x - ox) * (p.x - ox) + (p.y - oy) * (p.y - oy));
          if (d > maxLitReachPx) maxLitReachPx = d;
        }
      }
    }
    // Clamp to base vision range if set (can't see further than base range even with light)
    const litCapped = visionRangePx > 0 ? Math.min(maxLitReachPx, visionRangePx) : maxLitReachPx;
    effectiveRangePx = Math.max(maxVisionPx, litCapped);
    // If still 0 but has base range, use base range
    if (effectiveRangePx === 0 && visionRangePx > 0) effectiveRangePx = visionRangePx;
  }

  return {
    brightRangePx,
    dimRangePx,
    effectiveRangePx,
    disabled: false,
  };
}
